import { useEffect, useState } from "react";
import { Navbar } from "@/components/Navbar";
import { Skeleton } from "@/components/ui/skeleton";
import api from "@/lib/api";
import { Eye, Users, Send, Car } from "lucide-react";

const FALLBACK = "https://images.unsplash.com/photo-1533630217389-3a5e4dff5683?crop=entropy&cs=srgb&fm=jpg&q=85&w=200";

const StatCard = ({ icon: Icon, label, value, tone, testId }) => (
  <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5" data-testid={testId}>
    <div className={`flex h-9 w-9 items-center justify-center rounded-md ${tone}`}><Icon className="h-5 w-5" /></div>
    <p className="mt-3 font-display text-3xl font-black tracking-tight text-slate-950 dark:text-slate-50">{value}</p>
    <p className="text-sm font-semibold text-slate-500 dark:text-slate-400">{label}</p>
  </div>
);

export default function PartnerAnalytics() {
  const [data, setData] = useState(null);

  useEffect(() => { api.get("/partner/analytics").then(({ data }) => setData(data)); }, []);

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
      <Navbar />
      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6">
        <h1 className="font-display text-3xl font-bold tracking-tight text-slate-950 dark:text-slate-50" data-testid="analytics-title">Listing performance</h1>
        <p className="mt-1 text-slate-600 dark:text-slate-300">See how buyers are engaging with the vehicles you list.</p>

        {data === null ? (
          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-28 rounded-xl" />)}</div>
        ) : (
          <>
            <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <StatCard testId="stat-vehicles" icon={Car} label="Vehicles listed" value={data.totals.vehicles} tone="bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300" />
              <StatCard testId="stat-views" icon={Eye} label="Total buyer views" value={data.totals.views} tone="bg-rose-50 text-rose-600" />
              <StatCard testId="stat-buyers" icon={Users} label="Unique buyers" value={data.totals.unique_buyers} tone="bg-indigo-50 text-indigo-700" />
              <StatCard testId="stat-requests" icon={Send} label="Requests received" value={data.totals.requests} tone="bg-emerald-50 text-emerald-700" />
            </div>

            {data.vehicles.length === 0 ? (
              <div className="mt-8 rounded-xl border border-dashed border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 p-12 text-center" data-testid="analytics-empty">
                <Car className="mx-auto h-10 w-10 text-slate-300" />
                <p className="mt-3 font-display text-lg font-bold text-slate-900 dark:text-slate-100">No listings yet</p>
                <p className="text-sm text-slate-500 dark:text-slate-400">Add vehicles to start collecting buyer interest.</p>
              </div>
            ) : (
              <div className="mt-8 overflow-hidden rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
                <table className="w-full text-sm">
                  <thead className="border-b border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950 text-left text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                    <tr><th className="px-5 py-3">Vehicle</th><th className="px-5 py-3 text-right">Views</th><th className="px-5 py-3 text-right">Unique buyers</th><th className="px-5 py-3 text-right">Requests</th></tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {data.vehicles.map((v) => (
                      <tr key={v.id} data-testid={`analytics-row-${v.id}`} className="hover:bg-slate-50">
                        <td className="px-5 py-3">
                          <div className="flex items-center gap-3">
                            <img src={v.image_url || FALLBACK} alt="" className="h-10 w-14 shrink-0 rounded-md border border-slate-200 dark:border-slate-800 object-cover" onError={(e) => { e.target.src = FALLBACK; }} />
                            <div>
                              <p className="font-semibold text-slate-900 dark:text-slate-100">{v.year} {v.make} {v.model}</p>
                              <p className="text-xs text-slate-500 dark:text-slate-400">{v.trim}{v.availability !== "available" ? " · Sold" : ""}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-5 py-3 text-right font-semibold text-slate-800 dark:text-slate-200">{v.views}</td>
                        <td className="px-5 py-3 text-right font-semibold text-slate-800 dark:text-slate-200">{v.unique_buyers}</td>
                        <td className="px-5 py-3 text-right font-semibold text-slate-800 dark:text-slate-200">{v.requests}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
